'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Grid } from '@achieve/ascend'
import { _get } from 'utils/shared'
import { MediaImageStandard } from 'components/Contentful'
import Section from 'components/Section'
import { Carousel } from 'components/Carousels'
import styles from './MultiSlider.module.scss'
import { Eyebrow } from 'components/Eyebrow'
import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { getPageContentTypes } from 'utils/environmentConfig'
import { WEBSITE_NAMES } from 'constants/websites'

// TODO - add schema validation
function MultiSlider({ content, classNameSection }) {
  const { eyebrow, sliderItems } = useMemoizedContentGetter(content, ['sliderItems', 'eyebrow'])
  const carouselItemsCollection = _get(sliderItems, 'carouselItemsCollection', [])
  const config = content?.fields?.additionalConfiguration?.jsonContent
  const isOne = carouselItemsCollection?.items?.length === 1
  const list =
    carouselItemsCollection?.items?.length > 1
      ? carouselItemsCollection?.items
      : carouselItemsCollection?.items?.[0]?.gridItemsCollection?.items

  const sliderRef = useRef(null)
  const trackRef = useRef(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const { websiteField } = getPageContentTypes()

  useEffect(() => {
    const checkScrollable = () => {
      if (sliderRef.current && trackRef.current) {
        setIsScrollable(trackRef.current.scrollWidth > window.innerWidth)
      }
    }
    checkScrollable()
    window.addEventListener('resize', checkScrollable)

    return () => {
      window.removeEventListener('resize', checkScrollable)
    }
  }, [sliderRef, trackRef])

  const SlideItem = ({ mediaAltText, mediaContent }) => {
    const imgWidth =
      typeof config?.imgWidth === 'number' ? config.imgWidth : Number(config?.imgWidth ?? 150)
    const imgHeight =
      typeof config?.imgHeight === 'number' ? config.imgHeight : Number(config?.imgHeight ?? 70)

    const resolvedWidth = Number.isFinite(imgWidth) && imgWidth > 0 ? imgWidth : 150
    const resolvedHeight = Number.isFinite(imgHeight) && imgHeight > 0 ? imgHeight : 70

    return (
      <Grid item className={styles['multi-slider-item']}>
        <span>
          <MediaImageStandard
            alt={mediaAltText}
            content={mediaContent}
            layout="fill"
            objectFit="contain"
            width={resolvedWidth}
            height={resolvedHeight}
            sizes={`${resolvedWidth}px`}
            data-testid="multi-slider-item-image"
          />
        </span>
      </Grid>
    )
  }

  function Slide({ item }) {
    const slideChildItems = _get(item, 'gridItemsCollection.items', [])
    return isOne ? (
      <div className={styles['multi-slider-one']} data-testid="multi-slider-one">
        <SlideItem
          key={item?.fieldName}
          mediaAltText={item?.mediaAltText}
          mediaContent={item?.mediaContent}
        />
      </div>
    ) : (
      <Grid
        container
        className={styles['multi-slider-slide']}
        alignItems="center"
        data-testid="multi-slider"
      >
        {slideChildItems.map((item, index) => (
          <span key={`item-${index}`}>
            <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
          </span>
        ))}
      </Grid>
    )
  }

  function InfinityLogos() {
    return (
      <div className="overflow-x-hidden w-full" data-testid="multi-slider-infinity-logos">
        <div
          ref={sliderRef}
          className={classNames('flex w-max whitespace-nowrap', {
            'animate-marquee': isScrollable,
            'justify-center mx-auto': !isScrollable,
          })}
          style={{
            '--marquee-duration': `${list.length * 2 * 3}s`, //calculate the speed all the elements and the copies, 3 seconds each
          }}
        >
          {/* First Set */}
          <div className={`flex ${config?.gap}`} ref={trackRef}>
            {list.map((item, index) => (
              <div className={styles['slide']} key={`set1-${index}`}>
                <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
              </div>
            ))}
          </div>
          {/* Identical Second Set For Seamless Transition*/}
          {isScrollable && (
            <div className={`flex ${config?.gap}`}>
              {list.map((item, index) => (
                <div className={styles['slide']} key={`set2-${index}`}>
                  <SlideItem mediaAltText={item?.mediaAltText} mediaContent={item?.mediaContent} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Section
      childContainerClassName={styles['multi-slider-container']}
      className={classNames([
        styles['multi-slider-section'],
        classNameSection,
        {
          'py-0 md:py-[64px]': config?.condensedSection,
          'pt-0!': config?.noPaddingTop,
        },
        config?.paddingY ? `py-[${Number(config?.paddingY)}px]` : '',
      ])}
      data-testid="multi-slider-section"
      data-narrow="true"
      relative={false}
      variant={config?.background}
    >
      {eyebrow && (
        <Eyebrow
          content={eyebrow?.textContent}
          black={config?.eyebrowBlack}
          variant="bodySm"
          fontWeight="bold"
          component={websiteField === WEBSITE_NAMES.BILLS ? 'p' : 'h3'}
          className={styles['multi-slider-eyebrow']}
        />
      )}
      <div
        className={classNames(styles['multi-slider'], {
          'max-w-[425px] lg:max-w-max flex flex-col justify-self-center': config?.gridLogos,
        })}
        data-carousel-variant={config?.carouselVariant}
      >
        {config?.loopLogos ? (
          <div className={styles['multi-slider-loop']} data-testid="multi-slider-loop">
            <InfinityLogos />
          </div>
        ) : config?.gridLogos ? (
          <div
            className={classNames('flex px-4 lg:px-[300px]', {
              'flex-row gap-4 lg:gap-6': config?.keepLogosInRow,
              'flex-col md:flex-row gap-1': !config?.keepLogosInRow,
            })}
            data-testid="multi-slider-grid"
          >
            {list?.map(({ mediaAltText, mediaContent }, index) => (
              <div
                key={`item-slide-${index}`}
                className={classNames('py-4 h-[130px] md:h-[100px]', {
                  'border-solid border-0 border-b border-b-gray-300 md:border-b-0':
                    index < list.length - 1 && !config?.keepLogosInRow,
                  'w-[150px]': config?.keepLogosInRow,
                })}
              >
                <MediaImageStandard
                  alt={mediaAltText}
                  content={mediaContent}
                  objectFit="contain"
                  data-testid="multi-slider-item-image"
                />
              </div>
            ))}
          </div>
        ) : (
          <Carousel
            carouselItems={list}
            itemComponent={Slide}
            variant="standard"
            idPrefix={`multi-slider`}
            classNameContainer={styles['multi-slider-carousel-container']}
            classNameIndicatorDots={styles['multi-slider-indicator-dots']}
            classNameItems={styles['multi-slider-wrapper-items']}
            classNameActiveDot={styles['multi-slider-active-dots']}
            withIndicators={config?.withIndicators}
            disableArrows={config?.disableArrows}
          />
        )}
      </div>
    </Section>
  )
}

export { MultiSlider }
export default MultiSlider
